import { AttributeType, GeometryType } from "@evergis/api";
import { IOption, Menu, Popup, useToggle } from "@evergis/ui";
import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { AREA_ATTRIBUTE, CENTROID_ATTRIBUTE, LINE_ATTRIBUTE } from "../../../../evergis/constants";
import { projectCalculatedAttribute } from "../../../../hooks/attributes/useAddCalculatedAttributes";
import { LayerAttribute } from "../../EditResource/components/Layer/LayerAttributeItem";
import { AddAttributeButton } from "./AddAttributeButton";
import { AttributesProps } from "./Attributes";
import { SYS_ATTRIBUTES_REQUEST } from "./const";
import { AddSystemAttributesLabel } from "./styled";
import { SystemAttributeOption } from "./types";

const DEFAULT_SYS_ATTRIBUTES_OPTIONS_MAP = {
  [SystemAttributeOption.DateOfCreate]: [SYS_ATTRIBUTES_REQUEST[0]],
  [SystemAttributeOption.DateOfEdit]: [SYS_ATTRIBUTES_REQUEST[1]],
  [SystemAttributeOption.Author]: [SYS_ATTRIBUTES_REQUEST[2]],
  [SystemAttributeOption.LastEditor]: [SYS_ATTRIBUTES_REQUEST[3]],
};

type AddSystemAttributesProps = Pick<AttributesProps, "addSysAttributes" | "sysAttributes" | "excludeSysAttributes"> & {
  withButton?: boolean;
  className?: string;
  geometryType?: GeometryType;
  geometryAttributeName?: string;
};

// eslint-disable-next-line max-lines-per-function
export const AddSystemAttributes: FC<AddSystemAttributesProps> = ({
  addSysAttributes,
  sysAttributes,
  excludeSysAttributes,
  withButton,
  className,
  geometryType,
  geometryAttributeName,
}) => {
  const { t } = useTranslation("common");
  const [isMenuOpen, toggleMenu] = useToggle(false);
  const sysOptionsMap = useMemo(() => {
    /* eslint-disable @typescript-eslint/no-explicit-any */
    let allSysOptionsMap: any = DEFAULT_SYS_ATTRIBUTES_OPTIONS_MAP;

    if (geometryType === GeometryType.Polygon) {
      const areaAttribute = {
        id: AREA_ATTRIBUTE,
        attributeName: AREA_ATTRIBUTE,
        alias: t("area"),
        type: AttributeType.Double,
        expression: `area(${geometryAttributeName})`,
        stringFormat: {
          rounding: 2,
          unitsLabel: t("sqMeter"),
          splitDigitGroup: true,
        },
        sysAttribute: true,
      };

      allSysOptionsMap = {
        ...allSysOptionsMap,
        [SystemAttributeOption.Area]: [areaAttribute],
        [SystemAttributeOption.All]: [areaAttribute, ...SYS_ATTRIBUTES_REQUEST],
      };
    } else if (geometryType === GeometryType.Polyline) {
      const lengthAttribute = {
        id: LINE_ATTRIBUTE,
        attributeName: LINE_ATTRIBUTE,
        alias: t("length"),
        type: AttributeType.Double,
        expression: `length(${geometryAttributeName})`,
        stringFormat: {
          rounding: 2,
          unitsLabel: t("meter"),
          splitDigitGroup: true,
        },
        sysAttribute: true,
      };

      allSysOptionsMap = {
        ...allSysOptionsMap,
        [SystemAttributeOption.Length]: [lengthAttribute],
        [SystemAttributeOption.All]: [lengthAttribute, ...SYS_ATTRIBUTES_REQUEST],
      };
    } else if (geometryType === GeometryType.Point) {
      const coordinatesAttribute = {
        id: CENTROID_ATTRIBUTE,
        attributeName: CENTROID_ATTRIBUTE,
        alias: t("coordinates"),
        type: AttributeType.Point,
        expression: projectCalculatedAttribute(geometryAttributeName, "coordinate"),
        stringFormat: {
          format: "DD.DD",
        },
        sysAttribute: true,
      };

      allSysOptionsMap = {
        ...allSysOptionsMap,
        [SystemAttributeOption.Coordinates]: [coordinatesAttribute],
        [SystemAttributeOption.All]: [coordinatesAttribute, ...SYS_ATTRIBUTES_REQUEST],
      };
    } else {
      allSysOptionsMap = {
        ...allSysOptionsMap,
        [SystemAttributeOption.All]: SYS_ATTRIBUTES_REQUEST,
      };
    }

    return allSysOptionsMap;
  }, [geometryType, geometryAttributeName, t]);
  const initialSystemAttributesOptions = useMemo(() => {
    let allSysAttributesOptions = [
      {
        text: "dateofcreate",
        description: t("date", { ns: "common" }),
        value: SystemAttributeOption.DateOfCreate,
      },
      {
        text: "dateofedit",
        description: t("date", { ns: "common" }),
        value: SystemAttributeOption.DateOfEdit,
      },
      {
        text: "author",
        description: t("string", { ns: "common" }),
        value: SystemAttributeOption.Author,
      },
      {
        text: "lasteditor",
        description: t("string", { ns: "common" }),
        value: SystemAttributeOption.LastEditor,
      },
      {
        text: t("dialogComponents.addAll", { ns: "dialogs" }),
        description: t("string", { ns: "common" }),
        value: SystemAttributeOption.All,
      },
    ];

    if (geometryType === GeometryType.Polygon) {
      allSysAttributesOptions = [
        {
          text: t("area"),
          description: t("area"),
          value: SystemAttributeOption.Area,
        },
        ...allSysAttributesOptions,
      ];
    } else if (geometryType === GeometryType.Polyline) {
      allSysAttributesOptions = [
        {
          text: t("length"),
          description: t("length"),
          value: SystemAttributeOption.Length,
        },
        ...allSysAttributesOptions,
      ];
    } else if (geometryType === GeometryType.Point) {
      allSysAttributesOptions = [
        {
          text: t("coordinates"),
          description: t("coordinates"),
          value: SystemAttributeOption.Coordinates,
        },
        ...allSysAttributesOptions,
      ];
    }

    return allSysAttributesOptions;
  }, [t, geometryType]);
  const [systemAttributesOptions, setSystemAttributesOptions] = useState(initialSystemAttributesOptions);
  const selectOption = useCallback(
    ([{ value }]: IOption[]) => {
      if (value === SystemAttributeOption.All) {
        const availableOptions = systemAttributesOptions.reduce((options, { value: optionValue }) => {
          return optionValue !== SystemAttributeOption.All
            ? [...options, ...sysOptionsMap[optionValue as SystemAttributeOption]]
            : options;
        }, []);

        addSysAttributes(availableOptions);
      } else {
        addSysAttributes(sysOptionsMap[value as SystemAttributeOption]);
      }
      toggleMenu();
    },
    [addSysAttributes, toggleMenu, systemAttributesOptions, sysOptionsMap],
  );

  useEffect(() => {
    if (sysAttributes && sysAttributes.length > 0) {
      const excludeOptionsValues = (sysAttributes as LayerAttribute[]).map(({ attributeName }) => attributeName);

      setSystemAttributesOptions(
        initialSystemAttributesOptions.filter(({ value }) => !excludeOptionsValues.includes(value)),
      );
    }
  }, [sysAttributes, initialSystemAttributesOptions]);

  const excludeSysAttributesStringed = JSON.stringify(excludeSysAttributes);
  const systemAttributesOptionsStringed = JSON.stringify(systemAttributesOptions);

  useEffect(() => {
    if (excludeSysAttributes && excludeSysAttributes.length > 0) {
      setSystemAttributesOptions(systemAttributesOptions.filter(({ value }) => !excludeSysAttributes.includes(value)));
    }
  }, [excludeSysAttributesStringed, systemAttributesOptionsStringed]);

  return systemAttributesOptions.length > 1 ? (
    <Popup
      placement={"top"}
      isVisible={isMenuOpen}
      onRequestClose={toggleMenu}
      anchor={ref =>
        withButton ? (
          <AddAttributeButton onClick={toggleMenu}>
            <AddSystemAttributesLabel ref={ref} onClick={toggleMenu} className={className}>
              {t("dialogComponents.systemAttributes1", { ns: "dialogs" })}
            </AddSystemAttributesLabel>
          </AddAttributeButton>
        ) : (
          <AddSystemAttributesLabel className={className} ref={ref} onClick={toggleMenu}>
            {t("dialogComponents.systemAttributes1", { ns: "dialogs" })}
          </AddSystemAttributesLabel>
        )
      }
    >
      <Menu options={systemAttributesOptions} selectOption={selectOption} />
    </Popup>
  ) : withButton ? (
    <AddAttributeButton disabled onClick={toggleMenu}>
      <AddSystemAttributesLabel className={className} disabled>
        {t("dialogComponents.systemAttributes1", { ns: "dialogs" })}
      </AddSystemAttributesLabel>
    </AddAttributeButton>
  ) : (
    <AddSystemAttributesLabel className={className} disabled>
      {t("dialogComponents.systemAttributes1", { ns: "dialogs" })}
    </AddSystemAttributesLabel>
  );
};
